import React, { useState, useEffect, useMemo } from "react";
import DynamicIcon from "../DynamicIcon";
import AddPoolElement from "../templates/AddPoolElement";
import { useSelector, useDispatch } from "react-redux";
import { selectAccountData } from "../../redux/main/accountSlice";
import CustomToggleSwitch from "../CustomToggleSwitch";
import { selectColorsSchema } from "../../redux/main/uiSlice";
import { useColorPalette } from "../../hooks/useColorPalette";
import { apiService } from "../../utils/apiService";
import Toastify from 'toastify-js';
import "toastify-js/src/toastify.css";
import CryptoJS from "crypto-js";
import ContactDeleteModal from "./ContactDeleteModal";
import { getNodeApiUrl } from "../../config/api.config";

const initialState = {
    name: "",
    surname: "",
    role: "",
    phone: "",
    mobile: "",
    email: "",
    contactTypeLabel: "",
    enabledToReservedArea: false,
    main: false,
    password: ""
}


export default function CustomerContactsPool({ arrayData, addEditContact, deleteContact, accountId, customerId }) {
    // Funzione per gestire l'eliminazione del contatto con modale di conferma
    const handleDeleteContact = (field, index) => {
        console.log('[DEBUG] handleDeleteContact - Inizio eliminazione:', { field, index });
        
        const contact = arrayData[index];
        console.log('[DEBUG] handleDeleteContact - Contatto da eliminare:', contact);
        
        // Mostra sempre la modale di conferma
        setContactToDelete(contact);
        setPendingDeleteInfo({ field, index });
        setIsDeleteModalOpen(true);
        console.log('[DEBUG] handleDeleteContact - Modale di conferma aperta');
    };
    
    // Funzione per procedere con l'eliminazione dopo la conferma
    const proceedWithDelete = () => {
        if (pendingDeleteInfo) {
            console.log('[DEBUG] proceedWithDelete - Eliminazione confermata:', pendingDeleteInfo);
            deleteContact(pendingDeleteInfo.field, pendingDeleteInfo.index);
            setIsDeleteModalOpen(false);
            setPendingDeleteInfo(null);
            setContactToDelete(null);
        }
    };
    // Hook per gestire i colori dinamici del tema
    const palette = useColorPalette();
    // Recupero i tipi di contatto e ruoli dalle impostazioni dell'account
    const accountData = useSelector(selectAccountData);
    const contactTypes = accountData?.settings?.contactTypes || [];
    const roleTypes = accountData?.settings?.roleTypes || [];
    
    // Gestisco l'apertura e chiusura del form
    // Questo stato passerà ai figli
    const [isAdding, setIsAdding] = useState(false);
    // Gestisco gli input del form
    const [formInput, setFormInput] = useState(initialState);
    // Stato per la modale di conferma cambio password
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    
    // Stato per la modale di invio credenziali
    const [showSendCredentialsModal, setShowSendCredentialsModal] = useState(false);
    const [sendingCredentials, setSendingCredentials] = useState(false);
    
    // Stato per la modale di conferma disabilitazione area riservata
    const [showDisableAccessModal, setShowDisableAccessModal] = useState(false);
    
    // Stati per la gestione della modale di conferma eliminazione contatto
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [pendingDeleteInfo, setPendingDeleteInfo] = useState(null);
    
    const handleFormInput = (event) => {
        const { id, value } = event?.target;
        setFormInput(prevState => ({
            ...prevState,
            [id]: value
        }))
    }
    // Gestisco la modalità edit
    const editInitialState = {
        isEditMode: true,
        index: null
    }
    const [edit, setEdit] = useState(editInitialState)
    // Gestisco il settaggio di edit
    const handleSetEdit = (index) => {
        setEdit({
            isEditMode: true,
            index
        });
        setIsAdding(true);
    }
    // Se edit è impostato, imposto i valori dell'oggetto all'indice specificato in formInput
    useEffect(() => {
        if (edit.isEditMode && edit.index !== null && arrayData[edit.index]) {
            // Assicuriamoci che enabledToReservedArea sia un valore booleano
            setFormInput({
                ...arrayData[edit.index],
                enabledToReservedArea: !!arrayData[edit.index].enabledToReservedArea,
                main: !!arrayData[edit.index].main
            })
        }
    }, [edit, arrayData]);
    // Gestisco l'annullamento della modalità edit (tornando alla modalità add e chiudendo il dropdown)
    const handleCancelEdit = () => {
        setEdit(editInitialState);
        setFormInput(initialState);
        setIsAdding(false);
    };
    // Se chiudo la modifica con lo switch, azzero formInput ed esco dalla modalità edit
    useEffect(() => {
        if (!isAdding) {
            setEdit(editInitialState);
            setFormInput(initialState);
        }
    }, [isAdding])

    // Controllo che sia presente l'email per attivare i pulsanti
    const [isAllField, setIsAllField] = useState(false);
    
    useEffect(() => {
        // Richiedi sempre email per abilitare i pulsanti, indipendentemente dallo stato dell'area riservata
        setIsAllField(!!formInput.email);
    }, [formInput]);

    // Stato per errori di validazione
    const [emailError, setEmailError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    
    // Funzione per verificare se l'email è già usata per area riservata
    const checkEmailDuplicate = async (email) => {
        if (!email || !formInput.enabledToReservedArea) return { hasConflict: false };
        
        setCheckingEmail(true);
        try {
            const url = getNodeApiUrl('/customers/check-reserved-emails');
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ 
                    emails: [email],
                    accountId: accountId,
                    excludeCustomerId: customerId
                })
            });
            
            if (!response.ok) {
                setCheckingEmail(false);
                return { hasConflict: false };
            }
            
            const result = await response.json();
            setCheckingEmail(false);
            return result;
        } catch (error) {
            console.error('[checkEmailDuplicate] Errore:', error);
            setCheckingEmail(false);
            return { hasConflict: false };
        }
    };

    // Il contatto principale è uno solo: riceve le mail di apertura assistenza (richiesta #021).
    // Toglie il flag dagli altri contatti; con mainIndex = null lo toglie a tutti
    const clearOtherMainContacts = (mainIndex) => {
        (arrayData || []).forEach((contact, index) => {
            if (index !== mainIndex && contact.main) {
                addEditContact('contactsList', { ...contact, main: false }, index);
            }
        });
    };

    // Imposta o toglie il contatto principale direttamente dalla tabella
    const handleToggleMainContact = (index) => {
        const contact = arrayData[index];
        if (contact.main) {
            addEditContact('contactsList', { ...contact, main: false }, index);
            return;
        }
        clearOtherMainContacts(index);
        addEditContact('contactsList', { ...contact, main: true }, index);
    };

    // Gestisco il tasto aggiungi, il quale deve anche chiudere la tendina di aggiunta indirizzo
    const handleFormSubmit = async () => {
        setEmailError(null);
        setPasswordError(null);
        
        // Se il contatto ha accesso area riservata, verifica che la password sia presente (solo per nuovi contatti)
        const isNewContact = !edit.isEditMode || edit.index === null;
        const originalContact = edit.index !== null ? arrayData[edit.index] : null;
        const wasAlreadyEnabled = originalContact?.enabledToReservedArea === true;
        
        if (formInput.enabledToReservedArea && isNewContact && !formInput.password) {
            setPasswordError('La password è obbligatoria per i contatti con accesso all\'area riservata');
            Toastify({
                text: 'Password obbligatoria per accesso area riservata',
                duration: 5000,
                close: true,
                gravity: "top",
                position: "right",
                stopOnFocus: true,
                style: {
                    background: "#dc3545",
                }
            }).showToast();
            return;
        }
        
        // Se in modifica e si sta abilitando l'area riservata per la prima volta, richiedi password
        if (formInput.enabledToReservedArea && !isNewContact && !wasAlreadyEnabled && !formInput.password) {
            setPasswordError('La password è obbligatoria per abilitare l\'accesso all\'area riservata');
            Toastify({
                text: 'Password obbligatoria per abilitare accesso area riservata',
                duration: 5000,
                close: true,
                gravity: "top",
                position: "right",
                stopOnFocus: true,
                style: {
                    background: "#dc3545",
                }
            }).showToast();
            return;
        }
        
        // Se il contatto ha accesso area riservata, verifica che l'email non sia già usata
        if (formInput.enabledToReservedArea && formInput.email) {
            const emailCheck = await checkEmailDuplicate(formInput.email);
            if (emailCheck.hasConflict) {
                setEmailError(emailCheck.conflictingEmails?.[0] || 'Email già utilizzata per accesso area riservata');
                Toastify({
                    text: `Email già utilizzata: ${emailCheck.conflictingEmails?.[0] || formInput.email}`,
                    duration: 5000,
                    close: true,
                    gravity: "top",
                    position: "right",
                    stopOnFocus: true,
                    style: {
                        background: "#dc3545",
                    }
                }).showToast();
                return; // Non procedere con il salvataggio
            }
        }
        
        // Prepara i dati del contatto da salvare
        let contactData = { ...formInput };
        
        // Se è abilitato all'area riservata e c'è una password, crittografala
        if (formInput.enabledToReservedArea && formInput.password) {
            contactData.password = CryptoJS.AES.encrypt(
                formInput.password,
                import.meta.env.VITE_ENCKEY
            ).toString();
        }
        
        if (contactData.main) {
            clearOtherMainContacts(edit.isEditMode && edit.index !== null ? edit.index : null);
        }

        if (edit.isEditMode && edit.index !== null) {
            addEditContact('contactsList', contactData, edit.index);
            setFormInput(initialState);
            setIsAdding(false);

        } else {
            addEditContact('contactsList', contactData);
            setFormInput(initialState);
            setIsAdding(false);
        }
    }
    return (
        <div className="w-full bg-slate-200 p-1">
            {/* Sezione intestazione con titolo e pulsante */}
            <div className="bg-white p-4 flex justify-between items-center">
                <h5 className="text-lg font-medium text-gray-700">Contatti</h5>
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    type="button"
                    style={{ 
                        backgroundColor: palette.primary,
                        color: palette.textOnPrimary
                    }}
                    className="flex items-center px-3 py-2 text-sm font-medium border border-transparent rounded-md shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                    <i className="fa-solid fa-plus mr-2"></i>
                    {edit.isEditMode && edit.index !== null ? 'MODIFICA CONTATTO' : 'AGGIUNGI CONTATTO'}
                </button>
            </div>

            {/* Form per l'aggiunta/modifica del contatto */}
            <div className={`bg-white overflow-hidden transition-all duration-300 ${isAdding ? 'max-h-[2000px] opacity-100 p-4 border-t border-gray-200' : 'max-h-0 opacity-0 p-0'}`}>
                    <div className="mt-4 flex flex-col gap-4">
                        <div className="row g-3 mb-3">
                            <div className="col-md-4">
                                <label htmlFor="name" className="form-label">Nome</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    id="name" 
                                    onChange={handleFormInput} 
                                    value={formInput.name} 
                                    placeholder="Inserisci nome"
                                />
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="surname" className="form-label">Cognome</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    id="surname" 
                                    onChange={handleFormInput} 
                                    value={formInput.surname} 
                                    placeholder="Inserisci cognome"
                                />
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="role" className="form-label">Ruolo</label>
                                <select 
                                    id="role" 
                                    onChange={handleFormInput} 
                                    value={formInput.role}
                                    className="form-select"
                                >
                                    <option value="">Seleziona ruolo</option>
                                    {roleTypes.map((type, index) => (
                                        <option key={index} value={type.label}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="phone" className="form-label">Telefono</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-phone"></i></span>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        id="phone" 
                                        onChange={handleFormInput} 
                                        value={formInput.phone} 
                                        placeholder="Inserisci telefono"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="mobile" className="form-label">Cellulare</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-mobile-alt"></i></span>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        id="mobile" 
                                        onChange={handleFormInput} 
                                        value={formInput.mobile} 
                                        placeholder="Inserisci cellulare"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="email" className="form-label">Email {formInput.enabledToReservedArea && <span className="text-red-500">*</span>}</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-envelope"></i></span>
                                    <input 
                                        type="email" 
                                        className={`form-control ${emailError ? 'border-danger' : ''}`}
                                        id="email" 
                                        onChange={(e) => {
                                            handleFormInput(e);
                                            setEmailError(null); // Reset errore quando l'utente modifica
                                        }} 
                                        value={formInput.email} 
                                        placeholder="Inserisci email"
                                    />
                                </div>
                                {emailError && (
                                    <div className="text-danger mt-1 text-sm">
                                        <i className="fa-solid fa-exclamation-circle me-1"></i>
                                        {emailError}
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="contactTypeLabel" className="form-label">Tipo di contatto</label>
                                <select 
                                    id="contactTypeLabel" 
                                    onChange={handleFormInput} 
                                    value={formInput.contactTypeLabel}
                                    className="form-select"
                                >
                                    <option value="">Seleziona tipo di contatto</option>
                                    {contactTypes.map((type, index) => (
                                        <option key={index} value={type.label}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        {/* Campo main: contatto principale */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2">
                                <CustomToggleSwitch
                                    onChange={(enabled) => {
                                        setFormInput(prev => ({
                                            ...prev,
                                            main: enabled
                                        }));
                                    }}
                                    value={formInput?.main || false}
                                />
                                <label className="text-sm font-medium ml-1">Contatto principale</label>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 mb-0">
                                Riceve le mail di apertura delle assistenze. Ce n&apos;è uno solo per cliente: se nessuno è principale, le mail vanno al primo contatto della lista.
                            </p>
                        </div>

                        {/* Campo enabledToReservedArea */}
                        <div className="mb-3">
                            <div className="flex items-center flex-wrap gap-4">
                                <div className="flex items-center gap-2 min-w-[250px]">
                                    <CustomToggleSwitch
                                        onChange={(enabled) => {
                                            // Se stiamo disabilitando un contatto che aveva già l'area riservata
                                            const originalContact = edit.index !== null ? arrayData[edit.index] : null;
                                            const wasEnabled = originalContact?.enabledToReservedArea === true;
                                            
                                            if (!enabled && wasEnabled && edit.isEditMode) {
                                                // Mostra modale di conferma prima di disabilitare
                                                setShowDisableAccessModal(true);
                                            } else {
                                                setFormInput(prev => ({
                                                    ...prev,
                                                    enabledToReservedArea: enabled
                                                }));
                                            }
                                        }}
                                        value={formInput?.enabledToReservedArea || false}
                                    />
                                    <label className="text-sm font-medium ml-1">Abilita accesso all'area riservata</label>
                                </div>
                                
                                <div className="flex-1 min-w-[200px]">
                                    <label htmlFor="password" className="form-label">
                                        {(edit.isEditMode && formInput.enabledToReservedArea) ? "" : "Password"} 
                                        {(formInput.enabledToReservedArea && !edit.isEditMode) && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="d-flex align-items-center">
                                        {/* Modalità creazione nuovo contatto */}
                                        {!edit.isEditMode && (
                                            <div>
                                                <div className="input-group">
                                                    <span className="input-group-text"><i className="fa-solid fa-lock"></i></span>
                                                    <input 
                                                        type="password" 
                                                        className={`form-control ${passwordError ? 'border-danger' : ''}`}
                                                        id="password" 
                                                        onChange={(e) => {
                                                            handleFormInput(e);
                                                            setPasswordError(null);
                                                        }} 
                                                        value={formInput.password || ''} 
                                                        placeholder="Inserisci password"
                                                        disabled={!formInput.enabledToReservedArea}
                                                    />
                                                </div>
                                                {passwordError && (
                                                    <div className="text-danger mt-1 text-sm">
                                                        <i className="fa-solid fa-exclamation-circle me-1"></i>
                                                        {passwordError}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Modalità modifica contatto */}
                                        {edit.isEditMode && (
                                            <div>
                                                {/* Verifica il valore originale dal contatto */}
                                                {edit.index !== null && arrayData[edit.index] && arrayData[edit.index].enabledToReservedArea === true ? (
                                                    /* Il contatto originale era già abilitato: mostra bottone cambia password e invia credenziali */
                                                    <div className="d-flex align-items-center gap-2 w-100 flex-wrap">
                                                        <span className="text-secondary"><i className="fa-solid fa-lock me-2"></i>Password esistente</span>
                                                        <div className="d-flex gap-2 ms-auto">
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => {
                                                                    setNewPassword("");
                                                                    setShowPasswordModal(true);
                                                                }}
                                                            >
                                                                <i className="fa-solid fa-key me-1"></i> Cambia
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={() => setShowSendCredentialsModal(true)}
                                                                title="Invia credenziali di accesso via email"
                                                            >
                                                                <i className="fa-solid fa-paper-plane me-1"></i> Invia credenziali
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    /* Il contatto originale non era abilitato: mostra campo password */
                                                    <div>
                                                        <div className="input-group">
                                                            <span className="input-group-text"><i className="fa-solid fa-lock"></i></span>
                                                            <input 
                                                                type="password" 
                                                                className={`form-control ${passwordError ? 'border-danger' : ''}`}
                                                                id="password" 
                                                                onChange={(e) => {
                                                                    handleFormInput(e);
                                                                    setPasswordError(null);
                                                                }} 
                                                                value={formInput.password || ''} 
                                                                placeholder="Inserisci password"
                                                                disabled={!formInput.enabledToReservedArea}
                                                            />
                                                        </div>
                                                        {passwordError && (
                                                            <div className="text-danger mt-1 text-sm">
                                                                <i className="fa-solid fa-exclamation-circle me-1"></i>
                                                                {passwordError}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {formInput.enabledToReservedArea && (!formInput.email && !formInput.mobile) && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
                                    <i className="fa-solid fa-info-circle mr-1"></i>
                                    Per abilitare l'accesso all'area riservata è necessario inserire almeno Email o Cellulare.
                                </div>
                            )}
                            
                            {/* Dialog personalizzato per il cambio password */}
                            {showPasswordModal && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
                                    <div className="relative bg-white rounded-lg shadow max-w-md w-full mx-4">
                                        {/* Header */}
                                        <div className="flex justify-between items-center p-4 border-b">
                                            <h3 className="text-lg font-medium">Cambia Password</h3>
                                            <button 
                                                type="button" 
                                                className="text-gray-400 hover:text-gray-700 focus:outline-none"
                                                onClick={() => setShowPasswordModal(false)}
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        </div>
                                        
                                        {/* Body */}
                                        <div className="p-4">
                                            <p className="mb-3">Stai modificando la password per il contatto: <strong>{formInput.name} {formInput.surname}</strong></p>
                                            
                                            <div className="mb-3">
                                                <label htmlFor="newPassword" className="block mb-1 text-sm font-medium">Nuova Password</label>
                                                <div className="flex">
                                                    <span className="inline-flex items-center px-3 bg-gray-200 border border-r-0 border-gray-300 rounded-l-md">
                                                        <i className="fa-solid fa-lock"></i>
                                                    </span>
                                                    <input
                                                        type="password"
                                                        className="flex-1 border border-gray-300 rounded-r-md py-1 px-2 focus:outline-none focus:ring-1 focus:border-blue-500"
                                                        id="newPassword"
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        placeholder="Inserisci nuova password"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Footer */}
                                        <div className="flex justify-end p-4 border-t space-x-2">
                                            <button 
                                                type="button" 
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                                onClick={() => setShowPasswordModal(false)}
                                            >
                                                Annulla
                                            </button>
                                            <button 
                                                type="button" 
                                                className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!newPassword ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={async () => {
                                                    if (!newPassword) {
                                                        alert("Inserisci una password prima di salvarla");
                                                        return;
                                                    }
                                                    
                                                    try {
                                                        // Import del servizio API se non è già stato importato nel file
                                                        // Crittografa la password prima dell'invio come richiesto
                                                        const encryptedPassword = CryptoJS.AES.encrypt(
                                                            newPassword,
                                                            import.meta.env.VITE_ENCKEY
                                                        ).toString();
                                                        
                                                        // Utilizzo del servizio apiService per la chiamata standardizzata
                                                        const response = await apiService.patch(
                                                            `${import.meta.env.VITE_API_BASE_URL}/identities/changeCustomerUserPassword`,
                                                            {
                                                                customerUserId: formInput._id,
                                                                password: encryptedPassword
                                                            },
                                                            {
                                                                headers: {
                                                                    'Content-Type': 'application/json',
                                                                    'auth-token': localStorage.getItem('installamelo-auth-token')
                                                                }
                                                            }
                                                        );
                                                        
                                                        // Il servizio apiService gestisce già gli errori, quindi questo blocco è semplificato
                                                        setShowPasswordModal(false);
                                                        setNewPassword("");
                                                        
                                                        // Mostra una notifica toast invece dell'alert
                                                        Toastify({
                                                            text: "Password aggiornata con successo",
                                                            duration: 3000,
                                                            close: true,
                                                            gravity: "top",
                                                            position: "right",
                                                            stopOnFocus: true,
                                                            style: {
                                                                background: "rgba(var(--success),1)",
                                                            }
                                                        }).showToast();
                                                    } catch (error) {
                                                        console.error("Errore durante l'aggiornamento della password:", error);
                                                        
                                                        // Mostra una notifica toast per l'errore invece dell'alert
                                                        Toastify({
                                                            text: "Si è verificato un errore durante l'aggiornamento della password",
                                                            duration: 4000,
                                                            close: true,
                                                            gravity: "top",
                                                            position: "right",
                                                            stopOnFocus: true,
                                                            style: {
                                                                background: "rgba(var(--danger),1)",
                                                            }
                                                        }).showToast();
                                                    }
                                                }}
                                                disabled={!newPassword}
                                            >
                                                Conferma
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Dialog per invio credenziali */}
                            {showSendCredentialsModal && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
                                    <div className="relative bg-white rounded-lg shadow max-w-md w-full mx-4">
                                        {/* Header */}
                                        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-green-500 to-green-600 rounded-t-lg">
                                            <h3 className="text-lg font-medium text-white">
                                                <i className="fa-solid fa-paper-plane me-2"></i>
                                                Invia Credenziali di Accesso
                                            </h3>
                                            <button 
                                                type="button" 
                                                className="text-white hover:text-gray-200 focus:outline-none"
                                                onClick={() => setShowSendCredentialsModal(false)}
                                                disabled={sendingCredentials}
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        </div>
                                        
                                        {/* Body */}
                                        <div className="p-5">
                                            <div className="flex items-start mb-4">
                                                <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                                                    <i className="fa-solid fa-exclamation-triangle text-amber-500 text-xl"></i>
                                                </div>
                                                <div className="ml-4">
                                                    <p className="text-gray-700 font-medium mb-2">
                                                        Stai per inviare le credenziali a:
                                                    </p>
                                                    <p className="text-gray-900 font-semibold">
                                                        {formInput.name} {formInput.surname}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        <i className="fa-solid fa-envelope me-1"></i>
                                                        {formInput.email}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                                <p className="text-amber-800 text-sm">
                                                    <i className="fa-solid fa-info-circle me-2"></i>
                                                    <strong>Attenzione:</strong> Questa operazione comporterà:
                                                </p>
                                                <ul className="mt-2 text-amber-700 text-sm list-disc list-inside space-y-1">
                                                    <li>Il reset della password attuale al valore temporaneo <code className="bg-amber-100 px-1 rounded">123456</code></li>
                                                    <li>L'invio di un'email con le nuove credenziali di accesso</li>
                                                </ul>
                                            </div>
                                            
                                            <p className="text-gray-600 text-sm">
                                                L'utente dovrà cambiare la password al primo accesso.
                                            </p>
                                        </div>
                                        
                                        {/* Footer */}
                                        <div className="flex justify-end p-4 border-t space-x-2 bg-gray-50 rounded-b-lg">
                                            <button 
                                                type="button" 
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                                onClick={() => setShowSendCredentialsModal(false)}
                                                disabled={sendingCredentials}
                                            >
                                                Annulla
                                            </button>
                                            <button 
                                                type="button" 
                                                className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center ${sendingCredentials ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                onClick={async () => {
                                                    if (!formInput.email) {
                                                        Toastify({
                                                            text: "Email non presente per questo contatto",
                                                            duration: 4000,
                                                            close: true,
                                                            gravity: "top",
                                                            position: "right",
                                                            style: { background: "rgba(var(--danger),1)" }
                                                        }).showToast();
                                                        return;
                                                    }
                                                    
                                                    setSendingCredentials(true);
                                                    try {
                                                        const response = await apiService.post(
                                                            `${getNodeApiUrl()}/identities/sendCredentials`,
                                                            {
                                                                customerUserId: formInput._id,
                                                                customerId: customerId,
                                                                email: formInput.email,
                                                                name: formInput.name,
                                                                surname: formInput.surname
                                                            },
                                                            {
                                                                headers: {
                                                                    'Content-Type': 'application/json',
                                                                    'auth-token': localStorage.getItem('installamelo-auth-token')
                                                                }
                                                            }
                                                        );
                                                        
                                                        setShowSendCredentialsModal(false);
                                                        
                                                        Toastify({
                                                            text: "Credenziali inviate con successo!",
                                                            duration: 4000,
                                                            close: true,
                                                            gravity: "top",
                                                            position: "right",
                                                            style: { background: "rgba(var(--success),1)" }
                                                        }).showToast();
                                                    } catch (error) {
                                                        console.error("Errore durante l'invio delle credenziali:", error);
                                                        Toastify({
                                                            text: error.message || "Errore durante l'invio delle credenziali",
                                                            duration: 4000,
                                                            close: true,
                                                            gravity: "top",
                                                            position: "right",
                                                            style: { background: "rgba(var(--danger),1)" }
                                                        }).showToast();
                                                    } finally {
                                                        setSendingCredentials(false);
                                                    }
                                                }}
                                                disabled={sendingCredentials}
                                            >
                                                {sendingCredentials ? (
                                                    <>
                                                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                                                        Invio in corso...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa-solid fa-paper-plane me-2"></i>
                                                        Conferma e Invia
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Dialog per conferma disabilitazione area riservata */}
                            {showDisableAccessModal && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
                                    <div className="relative bg-white rounded-lg shadow max-w-md w-full mx-4">
                                        {/* Header */}
                                        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-red-500 to-red-600 rounded-t-lg">
                                            <h3 className="text-lg font-medium text-white">
                                                <i className="fa-solid fa-user-slash me-2"></i>
                                                Disabilita Accesso Area Riservata
                                            </h3>
                                            <button 
                                                type="button" 
                                                className="text-white hover:text-gray-200 focus:outline-none"
                                                onClick={() => setShowDisableAccessModal(false)}
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        </div>
                                        
                                        {/* Body */}
                                        <div className="p-5">
                                            <div className="flex items-start mb-4">
                                                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                                    <i className="fa-solid fa-exclamation-triangle text-red-500 text-xl"></i>
                                                </div>
                                                <div className="ml-4">
                                                    <p className="text-gray-700 font-medium mb-2">
                                                        Stai per disabilitare l'accesso per:
                                                    </p>
                                                    <p className="text-gray-900 font-semibold">
                                                        {formInput.name} {formInput.surname}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        <i className="fa-solid fa-envelope me-1"></i>
                                                        {formInput.email || 'Email non specificata'}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                                <p className="text-red-800 text-sm">
                                                    <i className="fa-solid fa-warning me-2"></i>
                                                    <strong>Attenzione:</strong> Questa operazione comporterà:
                                                </p>
                                                <ul className="mt-2 text-red-700 text-sm list-disc list-inside space-y-1">
                                                    <li>L'eliminazione delle credenziali di accesso</li>
                                                    <li>L'utente non potrà più accedere all'area riservata</li>
                                                    <li>Tutti i dati di login verranno rimossi</li>
                                                </ul>
                                            </div>
                                            
                                            <p className="text-gray-600 text-sm">
                                                <i className="fa-solid fa-info-circle me-1"></i>
                                                Potrai riabilitare l'accesso in qualsiasi momento, ma dovrai impostare una nuova password.
                                            </p>
                                        </div>
                                        
                                        {/* Footer */}
                                        <div className="flex justify-end p-4 border-t space-x-2 bg-gray-50 rounded-b-lg">
                                            <button 
                                                type="button" 
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                                onClick={() => setShowDisableAccessModal(false)}
                                            >
                                                Annulla
                                            </button>
                                            <button 
                                                type="button" 
                                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                                                onClick={() => {
                                                    // Conferma la disabilitazione
                                                    setFormInput(prev => ({
                                                        ...prev,
                                                        enabledToReservedArea: false
                                                    }));
                                                    setShowDisableAccessModal(false);
                                                    
                                                    Toastify({
                                                        text: "Accesso all'area riservata verrà disabilitato al salvataggio",
                                                        duration: 3000,
                                                        close: true,
                                                        gravity: "top",
                                                        position: "right",
                                                        style: { background: "rgba(239, 68, 68, 0.9)" }
                                                    }).showToast();
                                                }}
                                            >
                                                <i className="fa-solid fa-user-slash me-2"></i>
                                                Conferma Disabilitazione
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="w-full flex justify-end mb-3 pb-2 pt-3 bg-white sticky bottom-0 border-t border-gray-200 shadow-sm z-20">
                            {
                                isAllField ? (
                                    <div className="flex gap-2">
                                        {/* Pulsante Annulla (sempre presente) */}
                                        <button 
                                            type="button"
                                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors" 
                                            onClick={() => {
                                                if (edit.isEditMode && edit.index !== null) {
                                                    handleCancelEdit();
                                                } else {
                                                    setIsAdding(false);
                                                    setFormInput(initialState);
                                                }
                                            }}
                                        >
                                            <i className="fa-solid fa-times mr-2"></i>Annulla
                                        </button>
                                        
                                        {/* Pulsante Aggiungi/Aggiorna in base al contesto */}
                                        <button 
                                            type="button"
                                            style={{ 
                                                backgroundColor: edit.isEditMode && edit.index !== null ? palette.secondary : palette.primary,
                                                color: edit.isEditMode && edit.index !== null ? palette.textOnSecondary : palette.textOnPrimary
                                            }}
                                            className="px-4 py-2 rounded-md transition-colors hover:opacity-90"
                                            onClick={handleFormSubmit}
                                        >
                                            {edit.isEditMode && edit.index !== null ? (
                                                <><i className="fa-solid fa-check mr-2"></i>Aggiorna</>
                                            ) : (
                                                <><i className="fa-solid fa-plus mr-2"></i>Aggiungi</>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        type="button"
                                        className="px-4 py-2 text-gray-500 bg-gray-200 rounded-md cursor-not-allowed opacity-60"
                                        disabled
                                    >
                                        <i className="fa-solid fa-ban mr-2"></i>
                                        {edit.isEditMode && edit.index !== null ? 'Aggiorna' : 'Aggiungi'}
                                    </button>
                                )
                            }
                        </div>
                    </div>
            </div>
            {/* Tabella di rendering elementi array */}
            <div className="p-4">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="h-8">
                            <th className="w-12">#</th>
                            <th className="">TIPO</th>
                            <th className="">NOME</th>
                            <th className="">COGNOME</th>
                            <th className="">RUOLO</th>
                            <th className="">TELEFONO</th>
                            <th className="">CELLULARE</th>
                            <th className="">EMAIL</th>
                            <th className="">AREA RISERVATA</th>
                            <th className="">PRINCIPALE</th>
                            <th className="">MODIFICA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            arrayData?.map((row, index) => {
                                return (
                                    < React.Fragment key={index} >
                                        <tr className="h-8 border-t border-slate-500 border-dashed">
                                            <td className="w-12">{index + 1}</td>
                                            <td className="">{row.contactTypeLabel}</td>
                                            <td className="">{row.name}</td>
                                            <td className="">{row.surname}</td>
                                            <td className="">{row.role}</td>
                                            <td className="">{row.phone}</td>
                                            <td className="">{row.mobile}</td>
                                            <td className="">{row.email}</td>
                                            <td className="">
                                                {row.enabledToReservedArea ? 
                                                    <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs">Abilitato</span> : 
                                                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">Non abilitato</span>
                                                }
                                            </td>
                                            <td className="">
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleMainContact(index)}
                                                    title={row.main ? "Contatto principale: riceve le mail delle assistenze" : "Imposta come contatto principale"}
                                                    style={{ color: row.main ? palette.primary : undefined }}
                                                    className={`text-base ${row.main ? '' : 'text-gray-300 hover:text-gray-500'}`}
                                                >
                                                    <i className={row.main ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                                                </button>
                                            </td>
                                            <td className="flex gap-2">
                                                <div onClick={() => handleSetEdit(index)}><DynamicIcon iconClass="fa-solid fa-pen-to-square" /></div>
                                                <div onClick={() => handleDeleteContact('contactsList', index)}><DynamicIcon iconClass="fa-solid fa-trash" /></div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )

                            })
                        }
                    </tbody>
                </table>
            </div>
            
            {/* Modale per conferma eliminazione contatto */}
            <ContactDeleteModal
                isOpen={isDeleteModalOpen}
                toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
                contactToDelete={contactToDelete}
                onConfirm={proceedWithDelete}
            />

        </div >
    )
}